/**
 * Event data must adhere to defined schema, otherwise, event tracking will fail
 * Schema defintion: https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/shopping_finished.yaml
 *
 * Currently supports server-side event firing only
 * To enable client-side event firing, add the app-config values used in this file to under the BROWSER key
 */

import _ from 'lodash';

import datadog from '@nerdwallet/datadog';
import appConfig from '@nerdwallet/app-config';

import * as segment from './segment-sdk';
import verticalDefinitions from '../config/vertical-definitions';
import {
  createClickHeader,
  extractHeaderInformation,
  redactEventData,
} from './redirect-utils';
import { logError, logInfo } from './logging-utils';
import { validateMarketplaceToken } from './validate-marketplace-token';

const UNKNOWN_BUG = 'unknown_bug';
const DEFAULT_CTA_TYPE = UNKNOWN_BUG;
const DEFAULT_DESTINATION_URL =
  'https://BUG-ic-is-attempting-to-redirect-but-a-destination-url-has-not-been-set.com/index.html';
const DEFAULT_FINISH_TYPE = UNKNOWN_BUG;
const NULL_UUID = '00000000-0000-0000-0000-000000000000';
const MISSING = 'Missing';
const DEFAULT_MONETIZABLE_TYPE = UNKNOWN_BUG;

const SHOPPING_FINISHED_EVENT_NAME = 'shopping_finished';
const SEGMENT_SHOPPING_FINISHED_NAME = 'Shopping Finished';

const MONETIZABLE_TYPES_ALLOWED = [
  'yes_assumed',
  'no',
  'no_because_of_limit',
  'no_because_of_lead',
];

const CTA_TYPES_ALLOWED = [
  'apply_now_button',
  'product_name',
  'product_image',
  'institution_name',
  'institution_image',
  'institution_website_link',
  'offer_name',
  'offer_link',
  'rates_and_fees_text',
  'rest_of_product_card',
];

const FINISH_TYPES_ALLOWED = [
  'external_application',
  'external_content',
  'internal_content',
  'lead_inputflow',
  'claim_prequal_offer',
  'after_lead_partner_website',
];

const PRODUCT_TYPES_ALLOWED = [
  'auto_loans',
  'banking',
  'credit_cards',
  'education_loans',
  'first_time_home_buyer_programs',
  'insurance',
  'investing',
  'mortgages',
  'personal_loans',
  'small_medium_business_loans',
  'taxes',
  'utilities',
];

const DRIVER_TYPES_ALLOWED = ['user_selected', 'recently_viewed', 'meid'];

// TODO: remove these mappings once beta-shopping-finish-event is deprecated.
// At that point the above types will be correct.
const CTA_MAPPING = {
  // Legacy linkTypes defined by ApplyNow component
  APPLY_LINK: 'offer_link',
  APPLY_NOW_BUTTON: 'apply_now_button',
  INSTITUTION_IMAGE: 'institution_image',
  OFFER_NAME: 'offer_name',
  PRODUCT_IMAGE: 'product_image',
  PRODUCT_NAME: 'product_name',
  // shopping_finished cta_type
  apply_now_button: 'apply_now_button',
  institution_image: 'institution_image',
  institution_name: 'institution_name',
  institution_website_link: 'institution_website_link',
  offer_link: 'offer_link',
  offer_name: 'offer_name',
  product_image: 'product_image',
  product_name: 'product_name',
  rates_and_fees_text: 'rates_and_fees_text',
  rest_of_product_card: 'rest_of_product_card',
};

const FINISH_TYPES_MAPPING = {
  external_application: 'external_application',
  external_application_referral: 'external_application',
  external_content: 'external_content',
  external_other_referral: 'external_content',
  internal_content: 'internal_content',
  internal_referral: 'internal_content',
  lead_inputflow: 'lead_inputflow',
  after_lead_partner_website: 'after_lead_partner_website',
};

const MONETIZABLE_TYPES_MAPPING = {
  NO: 'no',
  no: 'no',
  NO_BECAUSE_OF_LIMIT: 'no_because_of_limit',
  no_because_of_limit: 'no_because_of_limit',
  YES_ASSUMED: 'yes_assumed',
  no_because_of_lead: 'no_because_of_lead',
};

const PRODUCT_TYPES_MAPPING = {
  auto_loans: 'auto_loans',
  banking: 'banking',
  credit_cards: 'credit_cards',
  education_loans: 'education_loans',
  first_time_home_buyer_programs: 'first_time_home_buyer_programs',
  insurance: 'insurance',
  investing: 'investing',
  mortgages: 'mortgages',
  personal_loans: 'personal_loans',
  smb: 'small_medium_business_loans', // just for legacy reasons since this could be used.
  small_medium_business_loans: 'small_medium_business_loans',
  taxes: 'taxes',
  utilities: 'utilities',
};

// These are required fields in the URI for events to work correctly. In practice
// we won't require them in a production environment to actually do a redirect
// because there may be some content not providing all of these parameters to IC
// right now. In stage, we will be noisy about the missing parameters.

export const checkRequiredURIParameters = (req) => {
  const missingParameters = [];

  if (!_.get(req, `query.finish_type`)) {
    missingParameters.push('finish_type');
  }

  if (!_.get(req, `query.url`) && !_.get(req, `query.source_url`)) {
    missingParameters.push('source_url');
  }

  if (!_.get(req, `query.cta_type`) && !_.get(req, 'query.link_type')) {
    missingParameters.push('cta_type');
  }
  return missingParameters;
};

/**
 * Helper functions
 */
const getUrlPath = (url) => {
  return _.isString(url) ? url.replace(/\?.*$/, '') : null;
};

const getUrlAndReferrer = (req) => {
  return {
    referrer: getUrlPath(_.get(req, 'headers.referer', '')),
    url: getUrlPath(_.get(req, 'url', '')),
  };
};

const getRequestUrl = (req) => {
  const siteBaseURI = appConfig.SITE_BASE_URL;
  const url = getUrlPath(_.get(req, 'url', ''));
  const requestUrl = url
    ? `${siteBaseURI}${appConfig.BASE_URI}${url}`
    : siteBaseURI;
  if (requestUrl === siteBaseURI) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.missing_request_url'
    );
    logError(req, `${SHOPPING_FINISHED_EVENT_NAME}-event-missing-request-url`, {
      requestUrl,
    });
  }
  return requestUrl;
};

// Converts number strings into type number and evaluates all other values to null;
const convertToNumber = (value) => {
  let newValue = typeof value === 'string' ? _.trim(value) : value;

  if (!_.includes([undefined, null, '', false], newValue)) {
    newValue = _.toNumber(newValue);
    // eslint-disable-next-line no-restricted-globals -- directive added automatically by Shepherd migration
    return isNaN(newValue) ? null : newValue;
  }

  return null;
};

const createProductType = (req, loggerData) => {
  // try to infer the product_type firstly based on the path to /redirect, then by
  // the marketplace offer that we're using, then finally by the URI query param.
  let productType =
    loggerData.verticalProductType ||
    _.get(loggerData, 'product.marketplace_offer.product_type');

  productType = _.get(PRODUCT_TYPES_MAPPING, productType, UNKNOWN_BUG);

  if (!productType) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.product_type_missing'
    );
    logError(req, `${SHOPPING_FINISHED_EVENT_NAME}-event-product-type-missing`);
  } else if (!_.includes(PRODUCT_TYPES_ALLOWED, productType)) {
    datadog.increment(
      'interstitial-client.shopping-shopping_finished_event-event.errors.invalid_product_type'
    );
    logError(
      req,
      `${SHOPPING_FINISHED_EVENT_NAME}-event-invalid-product-type`,
      {
        ...getUrlAndReferrer(req),
        productType,
      }
    );
  }

  return productType;
};

const createSkuReferences = (req, loggerData, marketplaceRefs) => {
  const refs = {
    sku_source: 'marketplace',
  };
  if (marketplaceRefs.marketplace_offer_id) {
    refs.sku_level = 'offer';
    refs.sku_name = marketplaceRefs.marketplace_offer_name;
    refs.sku_id = marketplaceRefs.marketplace_offer_id;
  } else if (marketplaceRefs.marketplace_product_id) {
    refs.sku_level = 'product';
    refs.sku_name = marketplaceRefs.marketplace_product_name;
    refs.sku_id = marketplaceRefs.marketplace_product_id;
  } else if (marketplaceRefs.marketplace_institution_id) {
    refs.sku_level = 'institution';
    refs.sku_name = marketplaceRefs.marketplace_institution_name;
    refs.sku_id = marketplaceRefs.marketplace_institution_id;
  } else {
    refs.sku_level = MISSING;
    refs.sku_name = MISSING;
    refs.sku_id = MISSING;
  }
  return refs;
};

const createMarketplaceReferences = (req, loggerData) => {
  const marketplaceInstitutionId = _.get(
    loggerData,
    'product.marketplace_offer.product.institution.id',
    NULL_UUID
  );

  const marketplaceProductId = _.get(
    loggerData,
    'product.marketplace_offer.product.id',
    marketplaceInstitutionId === NULL_UUID ? NULL_UUID : null
  );

  const marketplaceOfferId = _.get(
    loggerData,
    'product.marketplace_offer.id',
    marketplaceProductId === NULL_UUID ? NULL_UUID : null
  );

  const marketplaceInstitutionName = _.get(
    loggerData,
    'product.marketplace_offer.product.institution.name',
    MISSING
  );

  const marketplaceProductName = _.get(
    loggerData,
    'product.marketplace_offer.product.name',
    MISSING
  );
  const marketplaceOfferName = _.get(
    loggerData,
    'product.marketplace_offer.name',
    MISSING
  );

  const marketplaceReferences = {
    marketplace_product_type: createProductType(req, loggerData),
    marketplace_institution_id: marketplaceInstitutionId,
    marketplace_institution_name: marketplaceInstitutionName,
    marketplace_product_id: marketplaceProductId,
    marketplace_product_name: marketplaceProductName,
    marketplace_offer_id: marketplaceOfferId,
    marketplace_offer_name: marketplaceOfferName,
  };
  if (
    !marketplaceReferences.marketplace_institution_id ||
    !marketplaceReferences.marketplace_product_id
  ) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.marketplace_references_missing'
    );

    logError(
      req,
      `${SHOPPING_FINISHED_EVENT_NAME}-event-marketplace-references-missing`,
      {
        eventName: SHOPPING_FINISHED_EVENT_NAME,
        marketplaceReferences,
      }
    );
  }
  return marketplaceReferences;
};

// Schema definition: https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/element_position_attributes.yaml
const createProductPlacementCoordinates = (req, clickHeader = {}) => {
  return {
    section_name:
      _.get(req, 'query.section_name') || clickHeader.productLocation || null,
    section_position: convertToNumber(
      _.get(req, 'query.section_position') ||
        clickHeader.sectionPosition ||
        null
    ),
    element_position: convertToNumber(
      _.get(req, 'query.element_position') ||
        _.get(req, 'query.product_position') ||
        clickHeader.productPosition ||
        null
    ),
  };
};

const createCtaType = (req, clickHeader = {}) => {
  let ctaType =
    _.get(req, 'query.cta_type') ||
    clickHeader.ctaType ||
    // linkType is a legacy field for ctaType
    _.get(req, 'query.link_type') ||
    clickHeader.linkType;
  ctaType = CTA_MAPPING[ctaType];
  if (!ctaType) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.cta_type_missing'
    );

    logError(req, `${SHOPPING_FINISHED_EVENT_NAME}-event-cta-type-missing`);
    return DEFAULT_CTA_TYPE;
  }

  if (!_.includes(CTA_TYPES_ALLOWED, ctaType)) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.invalid_cta_type'
    );

    logError(req, `${SHOPPING_FINISHED_EVENT_NAME}-event-invalid-cta-type`, {
      ...getUrlAndReferrer(req),
      ctaType,
    });
    return DEFAULT_CTA_TYPE;
  }
  return ctaType;
};

const createFinishType = (req) => {
  let finishType = _.get(req, 'query.finish_type');

  finishType = FINISH_TYPES_MAPPING[finishType];

  if (!finishType) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.finish_type_missing'
    );

    logError(req, `${SHOPPING_FINISHED_EVENT_NAME}-event-finish-type-missing`);
    return DEFAULT_FINISH_TYPE;
  }

  if (!_.includes(FINISH_TYPES_ALLOWED, finishType)) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.invalid_finish_type'
    );

    logError(req, `${SHOPPING_FINISHED_EVENT_NAME}-event-invalid-finish-type`, {
      ...getUrlAndReferrer(req),
      finishType,
    });
    return DEFAULT_FINISH_TYPE;
  }
  return finishType;
};

const createDestinationUrl = (req, loggerData) => {
  const destinationUrl = _.get(loggerData, 'product.url');

  if (!destinationUrl) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.destination_url_missing'
    );

    logError(
      req,
      `${SHOPPING_FINISHED_EVENT_NAME}-event-destination-url-missing`
    );
    return DEFAULT_DESTINATION_URL;
  }
  return destinationUrl;
};

const createMonetizable = (req, loggerData) => {
  let monetizable = _.get(req, 'query.monetizable');
  monetizable = MONETIZABLE_TYPES_MAPPING[monetizable];

  // Set monetizable to 'unknown_bug' for network-links wihout a corresponding marketplace product.
  if (
    _.get(loggerData, 'category') ===
      _.get(verticalDefinitions, 'network-links.category') &&
    !_.get(loggerData, 'product.marketplace_offer')
  ) {
    return UNKNOWN_BUG;
  }

  if (!monetizable) {
    monetizable =
      _.get(
        loggerData,
        'product.marketplace_offer.details.is_monetizing.value'
      ) || _.get(loggerData, 'monetizing');

    monetizable = monetizable ? 'yes_assumed' : 'no';
  }

  if (!_.includes(MONETIZABLE_TYPES_ALLOWED, monetizable)) {
    datadog.increment(
      'interstitial-client.shopping_finished_event.errors.invalid_monetizable_type'
    );

    logError(
      req,
      `${SHOPPING_FINISHED_EVENT_NAME}-event-invalid-monetizable-type`,
      {
        ...getUrlAndReferrer(req),
        monetizable,
      }
    );
    return DEFAULT_MONETIZABLE_TYPE;
  }

  return monetizable;
};

const createDriver = (req) => {
  const driver =
    _.get(req, 'query.driver') ||
    _.get(req, 'query.product_display_driver', null);
  if (driver) {
    if (!_.includes(DRIVER_TYPES_ALLOWED, driver)) {
      datadog.increment(
        'interstitial-client.shopping_finished_event.errors.invalid_driver_type'
      );
    }
  }
  return driver;
};

/**
 * MAIN FUNCTION: Fire a shopping finish event
 */
export default async (req, loggerData = {}) => {
  const headerInformation = extractHeaderInformation(req);
  const clickHeader = createClickHeader(req, loggerData);
  const cookieID = _.get(headerInformation, 'cookieId');
  const tokenToValidate = _.get(req, 'query.mvt');
  const marketplaceReferences = createMarketplaceReferences(req, loggerData);
  const skuReferences = createSkuReferences(
    req,
    loggerData,
    marketplaceReferences
  );
  const productPlacement = createProductPlacementCoordinates(req, clickHeader);
  const isTokenValid = validateMarketplaceToken(
    cookieID,
    marketplaceReferences.marketplace_offer_id,
    tokenToValidate,
    marketplaceReferences.marketplace_product_type
  );
  logInfo(
    req,
    `isTokenValid for ${cookieID} - ${_.get(isTokenValid, 'isValid')}`
  );

  // Get attribution parameters (utm_*) to establish traffic source
  const utmCampaign = _.get(req, 'query.utm_campaign');
  const utmContent = _.get(req, 'query.utm_content');
  const utmMedium = _.get(req, 'query.utm_medium');
  const utmSource = _.get(req, 'query.utm_source');
  const utmTerm = _.get(req, 'query.utm_term');

  const productImpressionId =
    _.get(req, 'query.product_impression_id') ||
    clickHeader.productImpressionId ||
    _.get(req, 'query.impression_id', null) ||
    clickHeader.impressionId ||
    _.get(req, 'query.sku_impression_id', null);

  // Set up actual event structure.
  // We try to get info we can from headerInformation first, then URI parameters
  // where it makes sense. Some fields should be overridable from the URI parameter

  // This is common event data that is being sent to both our internal tracking system and to Segment
  const commonEventData = {
    ...(utmCampaign && { utm_campaign: utmCampaign }),
    ...(utmContent && { utm_content: utmContent }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmSource && { utm_source: utmSource }),
    ...(utmTerm && { utm_term: utmTerm }),
    cta_type: createCtaType(req, clickHeader),
    destination_url: createDestinationUrl(req, loggerData),
    finish_type: createFinishType(req),
    monetizable: createMonetizable(req, loggerData),
    shopping_finish_id: loggerData.uniqueClickId,
  };
  const nwEventData = {
    ...commonEventData,
    product_impression_id: productImpressionId,
    marketplace_institution_id:
      marketplaceReferences.marketplace_institution_id,
    marketplace_product_id: marketplaceReferences.marketplace_product_id,
    marketplace_offer_id: marketplaceReferences.marketplace_offer_id,
    marketplace_product_type: marketplaceReferences.marketplace_product_type,
    ...productPlacement,
    bot_suspected: !_.get(isTokenValid, 'isValid', false),
    driver: createDriver(req),
    web_user_agent: _.get(headerInformation, 'userAgent'),
    page_number: convertToNumber(
      _.get(req, 'query.page_number', _.get(clickHeader, 'pageNumber', null))
    ),
    source_url_simple:
      getUrlPath(_.get(headerInformation, 'referrer', '')) ||
      getRequestUrl(req),
  };

  const segmentEventData = {
    ...commonEventData,
    ...skuReferences,
    calling_deployable_name: _.get(
      req,
      'query.calling_deployable_name',
      MISSING
    ),
    calling_deployable_version: _.get(
      req,
      'query.calling_deployable_version',
      MISSING
    ),
    // eslint-disable-next-line no-underscore-dangle
    deployable_name: appConfig._meta.deployable,
    // eslint-disable-next-line no-underscore-dangle
    deployable_version: appConfig._meta.version,
    entry_point_page_name: _.get(req, 'query.entry_point_page_name', MISSING),
    entry_point_page_url: _.get(req, 'query.entry_point_page_url', MISSING),
    entry_point_pageview_id: _.get(
      req,
      'query.entry_point_pageview_id',
      MISSING
    ),
    impression_page_name: _.get(req, 'query.impression_page_name', MISSING),
    impression_page_url:
      _.get(req, 'query.impression_page_url') || nwEventData.source_url_simple,
    impression_pageview_id: _.get(req, 'query.impression_pageview_id', MISSING),
    impression_position: _.get(req, 'query.impression_position', -1),
    impression_section:
      _.get(req, 'query.impression_section') || productPlacement.section_name,
    institution_id: marketplaceReferences.marketplace_institution_id,
    institution_name: marketplaceReferences.marketplace_institution_name,
    interstitial_client_bot_suspected: nwEventData.bot_suspected,
    offer_id: marketplaceReferences.marketplace_offer_id,
    offer_name: marketplaceReferences.marketplace_offer_name,
    product_id: marketplaceReferences.marketplace_product_id,
    product_name: marketplaceReferences.marketplace_product_name,
    redirect_url: _.get(req, 'originalUrl'),
    sku_impression_id:
      _.get(req, 'query.sku_impression_id') || productImpressionId,
    sku_product_type: marketplaceReferences.marketplace_product_type,
    sub_id_alias: _.get(req, 'query.sub_id_alias', null),
    traditional_device_id: cookieID,
  };

  const prequalId = _.get(req, 'query.prequal_id', null);
  if (prequalId != null) {
    nwEventData.prequal_id = prequalId;
  }

  const { cookieId, browserSessionId, pageViewId, userAgent, ip, referrer } =
    headerInformation;

  // Logging to help debug issues
  const eventLogData = {
    globalProps: {
      ip,
      referrer,
      userAgent,
      url: referrer || req.url,
      pageViewId: pageViewId || _.get(req, 'query.header_pageViewId'),
    },
    userProps: { cookieId, browserSessionId },
    eventData: redactEventData(nwEventData),
  };

  // Fire the finish events to segment and delphi.

  if (appConfig.SEGMENT.ENABLED) {
    // Yes this is a lot of verbose logging but we get so many people accusing
    // interstitial-client of not sending events, I feel this is necessary so we
    // can point to logs. We now print before we send the event and then after we
    // send the events (or not, in the case of segment, if finish_id was provided)

    logInfo(req, `before-fire-${SHOPPING_FINISHED_EVENT_NAME}-event-segment`);

    // if finish_id is provided in the URI parameters, we do not fire the segment
    // event, as it has already been fired by the forwarding application.
    const finishIdParam =
      _.get(req, 'query.shopping_finish_id', null) ||
      _.get(req, 'query.finish_id', null);

    if (finishIdParam === null) {
      // if no anonymousId, send a randomly generated one
      // otherwise grab existing to include in call to segment
      const segmentEvent = {
        event: SEGMENT_SHOPPING_FINISHED_NAME,
        anonymousId: _.get(req, 'cookies.ajs_anonymous_id') || NULL_UUID,
        properties: segmentEventData,
      };

      // suggested by segment to avoid the memory leaks. See https://github.com/segmentio/analytics-next/issues/860
      segment.track(segmentEvent, (err) => {
        if (err) {
          req.log.error({
            message: 'segment-event-failure',
            context: { err, segmentEvent },
          });
        }
      });

      logInfo(
        req,
        `after-fire-${SHOPPING_FINISHED_EVENT_NAME}-event-segment finish_id=${commonEventData.shopping_finish_id}`,
        segmentEvent
      );
    } else {
      // we log exactly what was passed into the application, commonEventData.shopping_finish_id contains
      // what is used in the event. They SHOULD be the same at this point.
      logInfo(
        req,
        `after-skip-${SHOPPING_FINISHED_EVENT_NAME}-event-segment finish_id=${finishIdParam}`
      );
    }
  } else {
    logInfo(
      req,
      `disabled segment ${SHOPPING_FINISHED_EVENT_NAME}-event. Not sending event`
    );
  }

  // Now we fire the delphi event, and log before and after. We always fire
  // delphi events, at least until its decommissioned.
  logInfo(req, `before-fire-${SHOPPING_FINISHED_EVENT_NAME}-event-delphi`);
  await req.analytics.track(SHOPPING_FINISHED_EVENT_NAME, nwEventData);
  logInfo(
    req,
    `after-fire-${SHOPPING_FINISHED_EVENT_NAME}-event-delphi finish_id=${commonEventData.shopping_finish_id}`,
    eventLogData
  );
};
